// Talmia 2.0 · Co-Pilot — large centered search pop-up over real data.
// One Smart Search bar queries everything; scope chips narrow it. Results show
// as rich per-section tables (columns/headers) with term highlighting. Click a
// row to deep-link into the record. Opens on ⌘K and the topbar button.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  Search, Package, ShoppingCart, Users, Target, FileText, PhoneCall,
  CheckSquare, BookOpen, Factory, Loader2, X, Rocket, Sparkles, ArrowUpRight,
} from "lucide-react";

import { OrderDetailModal } from "../pages/orders/OrdersDashboard";

const SCOPES = [
  { key: "",          label: "Smart",     icon: Sparkles,     dot: "#f59e0b" },
  { key: "products",  label: "Products",  icon: Package,      dot: "#fb923c" },
  { key: "orders",    label: "Orders",    icon: ShoppingCart, dot: "#60a5fa" },
  { key: "customers", label: "Customers", icon: Users,        dot: "#34d399" },
  { key: "leads",     label: "Leads",     icon: Target,       dot: "#fbbf24" },
  { key: "quotes",    label: "Quotes",    icon: FileText,     dot: "#e2e8f0" },
  { key: "calls",     label: "Calls",     icon: PhoneCall,    dot: "#38bdf8" },
  { key: "tasks",     label: "Tasks",     icon: CheckSquare,  dot: "#a78bfa" },
  { key: "hub",       label: "Knowledge", icon: BookOpen,     dot: "#2dd4bf" },
];

// Per-type: icon, colour, section label, and ordered table columns.
// col = [fieldKey, header, flexGrow]
const TYPE_META = {
  product:  { icon: Package,      dot: "#fb923c", label: "Products",       cols: [["sku","SKU",0],["name","Name",3],["brand","Brand",1],["cost","Cost ex VAT",0],["price","Web ex VAT",0],["sold","Sold",0]] },
  order:    { icon: ShoppingCart, dot: "#60a5fa", label: "Orders",         cols: [["ref","Order",0],["customer","Customer",2],["company","Company",2],["postcode","Postcode",0],["value","Value",0],["status","Status",1],["date","Date",0]] },
  customer: { icon: Users,        dot: "#34d399", label: "Customers",      cols: [["name","Name",2],["company","Company",2],["tier","Tier",0],["ltv","LTV",0],["orders","Orders",0],["phone","Phone",1]] },
  lead:     { icon: Target,       dot: "#fbbf24", label: "Leads",          cols: [["name","Name",2],["company","Company",2],["source","Source",1],["stage","Stage",0],["phone","Phone",1],["am","Owner",1]] },
  quote:    { icon: FileText,     dot: "#e2e8f0", label: "Quotes",         cols: [["ref","Ref",1],["project","Project",2],["customer","Customer",2],["build","Build",1],["value","Value",0],["status","Status",1],["owner","Owner",1]] },
  call:     { icon: PhoneCall,    dot: "#38bdf8", label: "Calls",          cols: [["contact","Contact",1],["agent","Agent",1],["direction","Dir",0],["category","Category",1],["when","When",0],["summary","Summary",3]] },
  task:     { icon: CheckSquare,  dot: "#a78bfa", label: "Tasks",          cols: [["title","Task",3],["type","Type",0],["status","Status",0],["priority","Priority",0],["assignee","Assignee",1],["due","Due",0]] },
  supplier: { icon: Factory,      dot: "#f59e0b", label: "Suppliers",      cols: [["name","Name",2],["account","Account",1],["contact","Contact",1]] },
  hub:      { icon: BookOpen,     dot: "#2dd4bf", label: "Knowledge Base", cols: [["section","Section",1],["title","Title",2],["snippet","Snippet",4]] },
};

const RECENT_KEY = "t2.copilot.recent";
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
function pushRecent(term) {
  const c = (term || "").trim();
  if (c.length < 2) return;
  try {
    const prev = readRecent();
    localStorage.setItem(RECENT_KEY, JSON.stringify([c, ...prev.filter((t) => t.toLowerCase() !== c.toLowerCase())].slice(0, 8)));
  } catch { /* silent */ }
}

function HL({ text, term }) {
  const s = String(text ?? "");
  if (!term || !s) return <>{s}</>;
  const toks = term.trim().split(/\s+/).filter((t) => t.length >= 2);
  if (!toks.length) return <>{s}</>;
  const re = new RegExp(`(${toks.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  const parts = s.split(re);
  return <>{parts.map((p, i) => re.test(p) ? <mark key={i} style={{ background: "rgba(245,158,11,.22)", color: "#fcd34d", borderRadius: 2, padding: "0 1px" }}>{p}</mark> : <span key={i}>{p}</span>)}</>;
}

const flatten = (groups) => (groups || []).flatMap((g) => (g.items || []).map((it) => ({ ...it, _g: g.type })));

export default function CoPilotDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState([]);
  const [openOrderId, setOpenOrderId] = useState(null);
  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setScope(""); setGroups([]); setActive(0); setRecent(readRecent());
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    const term = q.trim();
    if (term.length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    debRef.current = setTimeout(async () => {
      try {
        const sp = new URLSearchParams({ q: term, limit: scope ? 40 : 8 });
        if (scope) sp.set("scope", scope);
        const { data } = await api.get(`/search/global?${sp.toString()}`);
        setGroups(data.groups || []); setActive(0);
      } catch { setGroups([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debRef.current);
  }, [q, scope, open]);

  const flat = useMemo(() => flatten(groups), [groups]);

  const go = useCallback((it) => {
    if (!it) return;
    pushRecent(q);
    if (it.type === "order") {
      setOpenOrderId(it.id);
      onClose?.();
      return;
    }
    onClose?.();
    if (it.type === "hub") {
      try { localStorage.setItem("hub.lastTopic", it.id); } catch { /* silent */ }
      navigate(`/hub?topic=${encodeURIComponent(it.id)}`);
      return;
    }
    if (it.href) navigate(it.href);
  }, [navigate, onClose, q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, Math.max(flat.length - 1, 0))); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      else if (e.key === "Enter" && flat[active]) { e.preventDefault(); go(flat[active]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, flat, active, onClose, go]);

  const orderModal = openOrderId
    ? <OrderDetailModal orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    : null;
  if (!open) return orderModal;
  const term = q.trim();
  const showRecent = term.length < 2 && recent.length > 0;
  const showIdle = term.length < 2 && recent.length === 0;
  const showEmpty = term.length >= 2 && !loading && flat.length === 0;

  return (
    <>
    {orderModal}
    <div
      data-testid="copilot-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(3,6,12,.72)", backdropFilter: "blur(3px)", zIndex: 90, display: "grid", placeItems: "start center", padding: "3vh 2vw" }}
    >
      <div
        data-testid="copilot-drawer"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 1360, height: "94vh", display: "flex", flexDirection: "column",
          background: "#0d1117", border: "1px solid rgba(255,255,255,.1)", borderRadius: 16,
          boxShadow: "0 40px 120px rgba(0,0,0,.6)", overflow: "hidden",
          animation: "cpPop .2s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <style>{`@keyframes cpPop{from{transform:translateY(-14px) scale(.985);opacity:.4}to{transform:none;opacity:1}}
        .cp-scope::-webkit-scrollbar{display:none}
        .cp-trow:hover{background:rgba(255,255,255,.05)!important}`}</style>

        {/* Header */}
        <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#f97316,#a855f7)" }}>
                <Rocket className="w-4 h-4" style={{ color: "#fff" }} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", lineHeight: 1 }}>Co-Pilot</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 3, fontFamily: "monospace" }}>Search everything in Talmia — click any result to open it</div>
              </div>
            </div>
            <button onClick={onClose} data-testid="copilot-close" aria-label="Close"
              style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", color: "#94a3b8", cursor: "pointer", display: "grid", placeItems: "center" }}>
              <X className="w-4.5 h-4.5" />
            </button>
          </div>

          <div style={{ position: "relative", marginTop: 14 }}>
            <Search className="w-5 h-5" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#f97316" }} />
            <input
              ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="copilot-input"
              placeholder={scope ? `Search ${SCOPES.find((s) => s.key === scope)?.label}…` : "Search products, orders, customers, leads, quotes, calls, tasks, hub… (try multiple words)"}
              style={{ width: "100%", padding: "13px 42px 13px 44px", background: "#161b22", border: "1px solid rgba(249,115,22,.5)", borderRadius: 11, color: "#fff", fontSize: 15, outline: "none" }}
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "#2dd4bf" }} />}
          </div>

          <div className="cp-scope" style={{ display: "flex", gap: 7, marginTop: 11, overflowX: "auto", scrollbarWidth: "none" }}>
            {SCOPES.map((s) => {
              const Icon = s.icon; const on = scope === s.key;
              return (
                <button key={s.key || "smart"} onClick={() => setScope(s.key)} data-testid={`copilot-scope-${s.key || "smart"}`}
                  style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                    border: `1px solid ${on ? s.dot : "rgba(255,255,255,.1)"}`, background: on ? `${s.dot}22` : "rgba(255,255,255,.02)", color: on ? s.dot : "#94a3b8", whiteSpace: "nowrap", transition: "all .15s" }}>
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="t2-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 18px 20px", minHeight: 0 }}>
          {showIdle && (
            <div style={{ textAlign: "center", padding: "90px 20px", color: "#64748b" }}>
              <Rocket className="w-9 h-9" style={{ margin: "0 auto 14px", color: "#f97316", opacity: .85 }} />
              <div style={{ fontWeight: 800, color: "#e2e8f0", fontSize: 18 }}>Ask Co-Pilot anything</div>
              <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.7 }}>Products · Orders · Customers · Leads · Quotes · Calls · Tasks · Knowledge Base</div>
              <div style={{ fontSize: 11.5, marginTop: 14, color: "#475569", fontFamily: "monospace" }}>Tip: type several words to narrow it · tap a chip to search one area</div>
            </div>
          )}

          {showRecent && (
            <div style={{ padding: "6px 2px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#475569", marginBottom: 9 }}>Recent searches</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {recent.map((r) => (
                  <button key={r} onClick={() => { setQ(r); inputRef.current?.focus(); }} data-testid={`copilot-recent-${r.replace(/\s+/g, "-")}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12.5, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", color: "#cbd5e1", cursor: "pointer" }}>
                    <Search className="w-3 h-3" style={{ opacity: .5 }} /> {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showEmpty && (
            <div style={{ textAlign: "center", padding: "80px 20px", color: "#64748b" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 15 }}>No matches for “{term}”</div>
              <div style={{ fontSize: 13, marginTop: 7 }}>Try a SKU, order #, postcode, name, or a hub keyword.</div>
            </div>
          )}

          {!loading && flat.length > 0 && (() => {
            let idx = -1;
            return groups.map((g) => {
              const itype = (g.items && g.items[0] && g.items[0].type) || g.type;
              const meta = TYPE_META[itype] || TYPE_META[g.type] || {};
              const Icon = meta.icon || Search;
              const cols = meta.cols || [["label", "Result", 3]];
              return (
                <div key={g.type} data-testid={`copilot-group-${g.type}`} style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px 8px" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.dot }} />
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.dot }} />
                    <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: meta.dot }}>{meta.label || g.label}</span>
                    <span style={{ marginLeft: 4, fontSize: 11, color: "#475569", fontFamily: "monospace" }}>{g.items.length}</span>
                  </div>
                  <div style={{ border: "1px solid rgba(255,255,255,.07)", borderRadius: 10, overflow: "hidden" }}>
                    {/* header */}
                    <div style={{ display: "flex", gap: 12, padding: "7px 14px", background: "rgba(255,255,255,.03)", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                      {cols.map(([k, h, flex]) => (
                        <div key={k} style={{ flex: `${flex || 0} 1 0`, minWidth: flex ? 60 : 56, fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#64748b" }}>{h}</div>
                      ))}
                      <div style={{ width: 18, flexShrink: 0 }} />
                    </div>
                    {g.items.map((it) => {
                      idx += 1; const i2 = idx; const isActive = i2 === active;
                      const f = it.fields || {};
                      return (
                        <div key={`${it.type}-${it.id}`} data-cp-idx={i2} className="cp-trow"
                          onMouseEnter={() => setActive(i2)} onClick={() => go(it)} data-testid={`copilot-row-${it.type}-${i2}`}
                          style={{ display: "flex", gap: 12, alignItems: "center", padding: "9px 14px", cursor: "pointer",
                            background: isActive ? "rgba(255,255,255,.05)" : "transparent",
                            borderBottom: "1px solid rgba(255,255,255,.04)", borderLeft: `2px solid ${isActive ? meta.dot : "transparent"}` }}>
                          {cols.map(([k, , flex], ci) => {
                            const val = f[k] != null && f[k] !== "" ? f[k] : (ci === 0 ? it.label : "—");
                            const strong = ci === 0;
                            return (
                              <div key={k} title={String(val)} style={{ flex: `${flex || 0} 1 0`, minWidth: flex ? 60 : 56, fontSize: strong ? 13 : 12,
                                fontWeight: strong ? 700 : 500, color: strong ? "#fff" : "#94a3b8",
                                whiteSpace: (flex || 0) >= 3 ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                lineHeight: (flex || 0) >= 3 ? 1.45 : 1.3 }}>
                                <HL text={val} term={term} />
                              </div>
                            );
                          })}
                          <ArrowUpRight className="w-3.5 h-3.5" style={{ color: isActive ? meta.dot : "#475569", flexShrink: 0 }} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Footer */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,.07)", padding: "9px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 11, color: "#475569", fontFamily: "monospace" }}>
          <span>↑ ↓ navigate · ↵ open · esc close</span>
          <span>{flat.length ? `${flat.length} result${flat.length === 1 ? "" : "s"}${scope ? "" : ` across ${groups.length} section${groups.length === 1 ? "" : "s"}`}` : "\u00A0"}</span>
        </div>
      </div>
    </div>
    </>
  );
}
